import { type ProColumns } from '@ant-design/pro-components'
import { Button } from 'antd'
import type { z } from 'zod'
import type { billSchema } from '@super-mega/server/contracts/bill'
import { TableRowActions } from '@/components/table-row-actions.tsx'
import { formatShanghaiDateTime } from '@/const/date.ts'
import { CREATED_AT_SORT_DIRECTIONS, TABLE_ACTION_COLUMN } from '@/const/list.ts'
import { RANGE_PRESETS } from '@/const/range-presets.ts'
import type { useMemberCreatorOptions } from '@/features/access/index.ts'
import { BusinessTag } from '@/features/master-data/index.ts'
import { UserAvatarName } from '@/features/profile/index.ts'
import { getTableSortOrder } from '@/utils/list-query.ts'
import { proRemoteSearch } from '@/utils/pro-select-search.ts'
import { type BillOperation } from './bill-action-dialog.tsx'
import { billDate, type BillSearch } from './bill-search.ts'
import { createBillNoteColumn, createBillSummaryColumns } from './bill-summary-columns.tsx'
import { billRowActions } from './const/bill-actions.ts'
import type { useBillFilterOptions } from './hooks/use-bill-filter-options.ts'
import { toCents } from './utils/money.ts'

type Row = z.infer<typeof billSchema>

type BillColumnsOptions = {
  search: BillSearch
  filterOptions: ReturnType<typeof useBillFilterOptions>
  creators: ReturnType<typeof useMemberCreatorOptions>
  canReadOrders: boolean
  canManage: boolean
  setDetailAction: (action: 'refund' | undefined) => void
  setDetailId: (id: number) => void
  setOperation: (operation: BillOperation) => void
}

export function createBillListColumns({
  search,
  filterOptions,
  creators,
  canReadOrders,
  canManage,
  setDetailAction,
  setDetailId,
  setOperation
}: BillColumnsOptions): ProColumns<Row>[] {
  const direction = 'receivable' as const
  const summaryColumns = createBillSummaryColumns({ direction, searchableStatus: true })
  return [
    {
      title: '当前归组',
      dataIndex: 'grouped',
      hideInTable: true,
      valueEnum: { yes: { text: '已归组' }, no: { text: '未归组' } }
    },
    {
      title: '归档状态',
      dataIndex: 'archived',
      hideInTable: true,
      valueType: 'select',
      valueEnum: { active: { text: '未归档' }, archived: { text: '已归档' }, all: { text: '全部' } }
    },
    {
      title: '业务类型',
      dataIndex: 'businessId',
      valueType: 'select',
      fieldProps: {
        allowClear: true,
        placeholder: '请选择业务类型',
        ...filterOptions.business,
        mode: 'multiple',
        maxTagCount: 'responsive'
      },
      hideInTable: true
    },
    {
      title: '关键词 / ID',
      order: 100,
      dataIndex: 'keyword',
      hideInTable: true,
      fieldProps: { allowClear: true, placeholder: '账单号（BL）、订单号（RO / SO）或 ID' }
    },
    {
      title: '客户',
      order: 90,
      dataIndex: 'customerId',
      hideInTable: true,
      valueType: 'select',
      fieldProps: {
        allowClear: true,
        placeholder: '请选择',
        ...filterOptions.customer,
        mode: 'multiple',
        maxTagCount: 'responsive'
      }
    },
    {
      title: '业务员',
      dataIndex: 'salespersonId',
      hideInTable: true,
      valueType: 'select',
      fieldProps: {
        allowClear: true,
        placeholder: '请选择',
        ...filterOptions.salesperson,
        mode: 'multiple',
        maxTagCount: 'responsive'
      }
    },
    {
      title: '经营主体',
      dataIndex: 'organizationId',
      hideInTable: true,
      valueType: 'select',
      fieldProps: {
        allowClear: true,
        placeholder: '请选择',
        ...filterOptions.organization,
        mode: 'multiple',
        maxTagCount: 'responsive'
      }
    },
    {
      title: '收款时间',
      dataIndex: 'receivedAtRange',
      hideInTable: true,
      fieldProps: { presets: RANGE_PRESETS },
      valueType: 'dateRange'
    },
    {
      title: '创建时间',
      dataIndex: 'createdAtRange',
      hideInTable: true,
      fieldProps: { presets: RANGE_PRESETS },
      valueType: 'dateRange'
    },
    {
      title: '创建人',
      dataIndex: 'creatorMemberId',
      hideInTable: true,
      valueType: 'select',
      fieldProps: {
        allowClear: true,
        options: creators.options,
        loading: creators.isLoading,
        ...proRemoteSearch(creators.onSearch)
      }
    },
    {
      title: '订单类型',
      dataIndex: 'orderType',
      hideInTable: true,
      valueEnum: { rental: { text: '租赁订单' }, service: { text: '服务订单' } }
    },
    ...summaryColumns.slice(0, 2),
    {
      title: '结算对象',
      dataIndex: 'customerName',
      search: false,
      width: '13rem'
    },
    { title: '经营主体', dataIndex: 'organizationName', search: false, width: '15rem' },
    ...summaryColumns.slice(2),
    {
      title: '最近收款日期',
      dataIndex: 'latestSettlementAt',
      search: false,
      width: '11rem',
      render: (_, row) => (row.latestSettlementAt ? formatShanghaiDateTime(row.latestSettlementAt) : '—')
    },
    {
      title: '费用期间',
      key: 'period',
      search: false,
      width: '14rem',
      render: (_, row) => `${billDate(row.periodStart)} 至 ${billDate(row.periodEnd)}`
    },
    createBillNoteColumn({ searchable: true }),
    {
      title: '订单号',
      dataIndex: 'sourceOrderNo',
      search: false,
      width: '12rem',
      render: (_, row) => {
        const orderId = row.orderType === 'rental' ? row.rentalOrderId : row.serviceOrderId
        if (!row.sourceOrderNo || !row.orderType || !orderId) {
          return '—'
        }
        return (
          <Button
            type="link"
            disabled={!canReadOrders}
            href={row.orderType === 'rental' ? `/orders/rentals/${orderId}` : `/orders/service/${orderId}/edit`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {row.sourceOrderNo}
          </Button>
        )
      }
    },
    {
      title: '业务类型',
      dataIndex: 'businessName',
      search: false,
      width: '10rem',
      ellipsis: true,
      render: (_, row) =>
        row.businessName ? <BusinessTag businessName={row.businessName} businessColor={row.businessColor} /> : '—'
    },
    {
      title: '业务员',
      dataIndex: 'salespersonName',
      search: false,
      width: '8rem',
      renderText: (value) => value || '—'
    },
    {
      title: '创建人',
      key: 'creator',
      search: false,
      width: '10rem',
      render: (_, row) =>
        row.creator ? (
          <UserAvatarName
            avatar={row.creator.avatar}
            name={row.creator.name}
            isSystem={row.creator.isSystem}
            size={28}
          />
        ) : (
          '—'
        )
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      search: false,
      width: '10.625rem',
      valueType: 'dateTime',
      sorter: true,
      sortDirections: CREATED_AT_SORT_DIRECTIONS,
      sortOrder: getTableSortOrder(search.sortOrder, search.sortField === 'createdAt')
    },
    { title: '归档时间', dataIndex: 'archivedAt', search: false, valueType: 'dateTime', width: '11rem' },
    {
      ...TABLE_ACTION_COLUMN,
      render: (_, row) => (
        <TableRowActions
          resource="bill"
          maxVisibleActions={1}
          actions={[
            {
              key: 'view',
              label: '查看',
              onClick: () => {
                setDetailAction(undefined)
                setDetailId(row.id)
              }
            },
            ...(canManage && row.status === 'issued' && toCents(row.receivedAmount) > toCents(row.refundedAmount)
              ? [
                  {
                    key: 'refund',
                    label: '登记退款',
                    onClick: () => {
                      setDetailAction('refund')
                      setDetailId(row.id)
                    }
                  }
                ]
              : []),
            ...(canManage
              ? billRowActions(row).map((item) => ({
                  ...item,
                  onClick: () =>
                    setOperation({ action: item.key, target: { mode: 'selected', ids: [row.id], direction } })
                }))
              : [])
          ]}
        />
      )
    }
  ]
}
